import { CalendarDays, CirclePause, RotateCcw, SkipForward } from 'lucide-react'
import { useState } from 'react'
import { formatCurrency } from '../../lib/money'
import type {
  MysteryBucketPlanAction,
  MysteryBucketService,
} from '../../types'

interface MysteryBucketPlanPreviewProps {
  readonly service: MysteryBucketService
  readonly compositionName: string
  readonly confirmed: boolean
  readonly inCart?: boolean
  readonly pending: boolean
  readonly onAddPlan: () => void
  readonly onReviewCart: () => void
  readonly onChangePlan: () => void
  readonly onPlanAction?: (action: MysteryBucketPlanAction) => void
  readonly onStartOver: () => void
}

function formatDrop(date: Date): string {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Australia/Brisbane',
  }).format(date)
}

function formatCutoff(date: Date): string {
  return `${new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Brisbane',
  }).format(date)}, 12:00 pm AEST`
}

export function MysteryBucketPlanPreview({
  service,
  compositionName,
  confirmed,
  inCart = false,
  pending,
  onAddPlan,
  onReviewCart,
  onChangePlan,
  onPlanAction,
  onStartOver,
}: MysteryBucketPlanPreviewProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const totalPerDrop =
    service.bucketSubtotalCents + service.deliveryFeeCents
  const status =
    service.lifecycleStatus === 'in_cart'
      ? 'active'
      : service.lifecycleStatus
  const skipped = Boolean(service.skippedDrop)
  const nextDrop = new Date(service.firstDropIso)
  const effectiveCutoff = new Date(service.cutoffIso)
  const changesOpen = Date.now() < effectiveCutoff.getTime()
  const undoSkipOpen =
    !service.skippedDrop ||
    Date.now() < new Date(service.skippedDrop.cutoffIso).getTime()
  const cadenceLabel =
    service.cadence === 'weekly' ? 'Every week' : 'Every fortnight'

  if (!confirmed) {
    return (
      <section
        className="mystery-plan-review"
        aria-labelledby="mystery-plan-review-title"
      >
        <div className="mystery-plan-review__heading">
          <p>{inCart ? 'Ready for checkout' : 'Service review'}</p>
          <h3 id="mystery-plan-review-title">
            {inCart
              ? 'Your plan is in the demo cart—not active yet.'
              : 'One clear plan, before anything goes in the cart.'}
          </h3>
        </div>
        <dl className="mystery-plan-review__facts">
          <div>
            <dt>Bucket</dt>
            <dd>{compositionName}</dd>
          </div>
          <div>
            <dt>Cadence</dt>
            <dd>{cadenceLabel}</dd>
          </div>
          <div>
            <dt>First drop</dt>
            <dd>{formatDrop(new Date(service.firstDropIso))}</dd>
          </div>
          <div>
            <dt>Delivery run</dt>
            <dd>{service.delivery.window}</dd>
          </div>
          <div>
            <dt>Postcode</dt>
            <dd>
              {service.postcode}{' '}
              <span>
                {service.zoneStatus === 'eligible'
                  ? 'pilot area'
                  : 'confirmation required'}
              </span>
            </dd>
          </div>
          <div>
            <dt>Change cut-off</dt>
            <dd>{service.cutoffLabel}</dd>
          </div>
        </dl>
        <div className="mystery-plan-review__price" aria-label="Per delivery price">
          <span>
            Bucket
            <strong>{formatCurrency(service.bucketSubtotalCents)}</strong>
          </span>
          <span>
            Local delivery
            <strong>{formatCurrency(service.deliveryFeeCents)}</strong>
          </span>
          <b>
            Per delivery
            <strong>{formatCurrency(totalPerDrop)}</strong>
          </b>
        </div>
        <div className="mystery-plan-review__controls">
          <span>
            <SkipForward aria-hidden="true" />
            Skip a drop
          </span>
          <span>
            <CirclePause aria-hidden="true" />
            Pause anytime
          </span>
          <span>
            <RotateCcw aria-hidden="true" />
            Cancel before cut-off
          </span>
        </div>
        <div className="mystery-plan-review__actions">
          <button
            className="bucket-button bucket-button--primary"
            type="button"
            disabled={pending}
            onClick={inCart ? onReviewCart : onAddPlan}
          >
            {pending
              ? 'Preparing plan…'
              : inCart
                ? 'Review subscription checkout'
                : `Add ${service.cadence} plan to demo cart`}
          </button>
          <button
            className="bucket-text-button"
            type="button"
            onClick={onChangePlan}
          >
            Change cadence or delivery run
          </button>
        </div>
        <p className="mystery-plan-review__disclaimer">
          Prototype only. No payment, order, subscription, delivery booking,
          email or SMS is created.
        </p>
      </section>
    )
  }

  return (
    <section
      className="mystery-plan-manager"
      aria-labelledby="mystery-plan-manager-title"
      data-plan-status={status}
    >
      <div className="mystery-plan-manager__status">
        <span>
          {status === 'active'
            ? 'Post-checkout account preview'
            : `${status} preview`}
        </span>
        <b>{cadenceLabel}</b>
      </div>
      <div className="mystery-plan-manager__heading">
        <CalendarDays aria-hidden="true" />
        <div>
          <p>Next Mystery Bucket</p>
          <h3 id="mystery-plan-manager-title">
            {status === 'cancelled'
              ? 'This plan preview is cancelled.'
              : status === 'paused'
                ? 'Deliveries are paused.'
                : formatDrop(nextDrop)}
          </h3>
          {status !== 'cancelled' && (
            <span>
              {service.delivery.window} · {formatCurrency(totalPerDrop)} per
              delivery
            </span>
          )}
        </div>
      </div>

      {status !== 'cancelled' ? (
        <>
          <div
            className="mystery-plan-manager__notice"
            role="status"
            aria-live="polite"
          >
            {skipped
              ? `The ${formatDrop(
                  new Date(service.skippedDrop!.firstDropIso),
                )} drop is skipped. The next previewed drop is ${formatDrop(
                  nextDrop,
                )}; changes close ${formatCutoff(effectiveCutoff)}.`
              : status === 'paused'
                ? 'Future drops are paused until you resume them.'
                : changesOpen
                  ? `Changes stay open until ${formatCutoff(effectiveCutoff)}.`
                  : `The ${formatCutoff(effectiveCutoff)} change cut-off has passed for this drop.`}
          </div>
          <div className="mystery-plan-manager__actions">
            <button
              type="button"
              disabled={
                status === 'paused' ||
                (skipped ? !undoSkipOpen : !changesOpen)
              }
              onClick={() => {
                const actionCutoff = skipped
                  ? service.skippedDrop?.cutoffIso
                  : service.cutoffIso
                if (
                  actionCutoff &&
                  Date.now() >= new Date(actionCutoff).getTime()
                ) {
                  return
                }
                onPlanAction?.(skipped ? 'undo_skip' : 'skip')
              }}
            >
              <SkipForward aria-hidden="true" />
              {skipped ? 'Undo next-bucket skip' : 'Skip next bucket'}
            </button>
            <button
              type="button"
              disabled={status !== 'paused' && !changesOpen}
              onClick={() => {
                if (
                  status !== 'paused' &&
                  Date.now() >= new Date(service.cutoffIso).getTime()
                ) {
                  return
                }
                onPlanAction?.(status === 'paused' ? 'resume' : 'pause')
              }}
            >
              <CirclePause aria-hidden="true" />
              {status === 'paused' ? 'Resume deliveries' : 'Pause deliveries'}
            </button>
            {!confirmingCancel ? (
              <button
                type="button"
                disabled={!changesOpen}
                onClick={() => setConfirmingCancel(true)}
              >
                <RotateCcw aria-hidden="true" />
                Cancel plan
              </button>
            ) : (
              <span className="mystery-plan-manager__cancel-confirm">
                <button
                  className="is-danger"
                  type="button"
                  onClick={() => {
                    if (Date.now() >= new Date(service.cutoffIso).getTime()) {
                      setConfirmingCancel(false)
                      return
                    }
                    onPlanAction?.('cancel')
                  }}
                >
                  Confirm cancellation
                </button>
                <button type="button" onClick={() => setConfirmingCancel(false)}>
                  Keep my plan
                </button>
              </span>
            )}
          </div>
          <button
            className="bucket-button bucket-button--primary"
            type="button"
            onClick={onReviewCart}
          >
            Review the per-delivery cart
          </button>
          <button
            className="bucket-text-button"
            type="button"
            onClick={onChangePlan}
          >
            Change cadence or delivery run
          </button>
        </>
      ) : (
        <button
          className="bucket-button bucket-button--primary"
          type="button"
          onClick={onStartOver}
        >
          Build a different plan
        </button>
      )}
      <p className="mystery-plan-review__disclaimer">
        These account controls are interactive demonstrations only.
      </p>
    </section>
  )
}
